import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

const allSettingsGroups = [
  {
    label: 'Sistema',
    items: [
      { label: 'Geral', path: '/settings/general', icon: 'ri-settings-3-line', adminOnly: true },
      { label: 'SMTP', path: '/settings/smtp', icon: 'ri-mail-send-line', adminOnly: true },
      { label: 'Usuários', path: '/settings/users', icon: 'ri-team-line', adminOnly: true },
      { label: 'Google OAuth', path: '/settings/google-oauth', icon: 'ri-google-line', adminOnly: true },
    ],
  },
  {
    label: 'Pagamentos',
    adminOnly: true,
    items: [
      { label: 'Mercado Pago', path: '/settings/mercadopago', icon: 'ri-bank-card-line', adminOnly: true },
      { label: 'Stripe', path: '/settings/stripe', icon: 'ri-bank-line', adminOnly: true },
    ],
  },
  {
    label: 'Envio',
    items: [
      { label: 'WhatsApp', path: '/settings/whatsapp', icon: 'ri-whatsapp-line' },
      { label: 'Anti-Spam', path: '/settings/anti-spam', icon: 'ri-shield-check-line' },
      { label: 'Evolution API', path: '/settings/evolution-api', icon: 'ri-server-line', adminOnly: true },
      { label: 'Meta API', path: '/settings/meta-api', icon: 'ri-meta-line', adminOnly: true },
    ],
  },
  {
    label: 'Infraestrutura',
    adminOnly: true,
    items: [
      { label: 'Infraestrutura', path: '/settings/infrastructure', icon: 'ri-computer-line', adminOnly: true },
      { label: 'Backup', path: '/settings/backup', icon: 'ri-archive-line', adminOnly: true },
      { label: 'Monitoramento', path: '/settings/monitoring', icon: 'ri-pulse-line', adminOnly: true },
      { label: 'Atualizações', path: '/settings/updates', icon: 'ri-download-cloud-2-line', adminOnly: true },
    ],
  },
];

export default function SettingsPage() {
  const location = useLocation();
  const { profile } = useAuth();
  const isAdmin = profile?.role === 'admin';

  // Filter groups based on admin status
  const settingsGroups = allSettingsGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => !item.adminOnly || isAdmin),
    }))
    .filter((group) => {
      // If the entire group is adminOnly, hide from non-admins
      if (group.adminOnly && !isAdmin) return false;
      // If all items were filtered out, hide the group
      if (group.items.length === 0) return false;
      return true;
    });

  // If on a sub-page, render the sidebar + content layout
  const isHub = location.pathname === '/settings';

  if (!isHub) {
    return (
      <div className="flex gap-0 min-h-[calc(100vh-180px)]">
        {/* Sidebar */}
        <aside className="w-[220px] flex-shrink-0 border-r border-background-200 pr-4">
          <div className="sticky top-0 pt-1">
            <Link
              to="/settings"
              className="flex items-center gap-2 text-xs font-medium text-foreground-500 hover:text-foreground-700 mb-4 px-3 py-1.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line" /> Voltar
            </Link>
            {settingsGroups.map((group) => (
              <div key={group.label} className="mb-4">
                <p className="text-[10px] font-bold text-foreground-400 uppercase tracking-wider px-3 mb-1.5">
                  {group.label}
                </p>
                {group.items.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-150 mb-0.5 whitespace-nowrap ${
                        isActive
                          ? 'bg-primary-50 text-primary-700 dark:bg-primary-500/10 dark:text-primary-400'
                          : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                      }`}
                    >
                      <i className={`${item.icon} text-base flex-shrink-0 ${isActive ? 'text-primary-600' : 'text-foreground-400'}`} />
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            ))}
          </div>
        </aside>

        {/* Content */}
        <div className="flex-1 min-w-0 pl-6">
          <Outlet />
        </div>
      </div>
    );
  }

  // Hub page with cards
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-heading font-bold text-foreground-950">Configurações</h2>
        <p className="text-sm text-foreground-500 mt-1">Gerencie todas as configurações da plataforma</p>
      </div>

      {settingsGroups.map((group) => (
        <div key={group.label}>
          <h3 className="text-xs font-bold text-foreground-400 uppercase tracking-wider mb-3">{group.label}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {group.items.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="bg-background-100 rounded-xl border border-background-200 p-4 hover:border-primary-300 transition-all group cursor-pointer flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-100 transition-colors">
                  <i className={`${item.icon} text-primary-600 text-lg`} />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground-900">{item.label}</p>
                  <p className="text-[11px] text-foreground-500 truncate">Configurar {item.label.toLowerCase()}</p>
                </div>
                <i className="ri-arrow-right-s-line text-foreground-300 group-hover:text-primary-500 ml-auto transition-colors" />
              </Link>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}